import { PlatformService } from './platformService';

export class MetadataService {
  // Metadaten von einer URL extrahieren
  static async extractMetadata(url: string, platformId: string): Promise<{
    title: string;
    description: string;
    image: string;
    author: string;
    publishedTime: string;
  }> {
    try {
      // Plattform-Webhook URL holen
      const platform = await PlatformService.getPlatformById(platformId);
      if (!platform.metadataWebhook) {
        throw new Error('Metadaten-Webhook ist für diese Plattform nicht konfiguriert');
      }

      const response = await fetch(platform.metadataWebhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Metadata extraction error:', error);
      throw new Error('Metadaten konnten nicht extrahiert werden');
    }
  }

  /**
   * Sendet Text an die Metadaten-API und versucht verschiedene CORS-Umgehungen
   */
  static async generateMetadata(
    text: string,
    categories?: string,
    dossiers?: string,
    userName?: string
  ): Promise<any> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 Sekunden Timeout
    
    // Request-Body mit allen verfügbaren Daten
    const requestBody: any = { text };
    if (categories) requestBody.kategorien = categories;
    if (dossiers) requestBody.dossiers = dossiers;
    if (userName) requestBody.benutzer = userName;
    
    try {
      // Verwende die erste verfügbare Plattform für den Webhook
      const platforms = await PlatformService.getAllPlatforms();
      const platform = platforms.find(p => p.metadataWebhook);
      
      if (!platform || !platform.metadataWebhook) {
        throw new Error('Keine Plattform mit konfiguriertem Metadaten-Webhook gefunden');
      }

      const response = await fetch(platform.metadataWebhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors',
        credentials: 'omit',
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Metadaten-API Timeout nach 30 Sekunden');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }
  
  /**
   * Alternative: Verwendet einen öffentlichen CORS-Proxy
   * WARNUNG: Nur für Entwicklung/Testing verwenden!
   */
  static async generateMetadataViaProxy(
    text: string,
    categories?: string,
    dossiers?: string,
    userName?: string
  ): Promise<any> {
    const proxyUrl = 'https://cors-anywhere.herokuapp.com/';
    
    // Request-Body mit allen verfügbaren Daten
    const requestBody: any = { text };
    if (categories) requestBody.kategorien = categories;
    if (dossiers) requestBody.dossiers = dossiers;
    if (userName) requestBody.benutzer = userName;
    
    try {
      // Verwende die erste verfügbare Plattform für den Webhook
      const platforms = await PlatformService.getAllPlatforms();
      const platform = platforms.find(p => p.metadataWebhook);
      
      if (!platform || !platform.metadataWebhook) {
        throw new Error('Keine Plattform mit konfiguriertem Metadaten-Webhook gefunden');
      }

      const response = await fetch(proxyUrl + platform.metadataWebhook, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        body: JSON.stringify(requestBody)
      });
      
      if (response.ok) {
        const result = await response.json();
        return result;
      } else {
        throw new Error(`Proxy-Fehler: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      throw error;
    }
  }
}
